import { Text, Flex, Switch, FormControl, Button, Icon } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type ModalFormData } from '../../schemas';
import { InputField } from '@/components/InputField';
import { TextareaField } from '@/components/TextareaField';
import { FunctionComponent } from 'react';
import { ChallengeStatus } from '@/model';
import { PiProhibit } from 'react-icons/pi';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

type Step1FieldsProps = {
  onStepSuccess?: VoidFunction;
  onCloseChallenge?: VoidFunction;
};

export const Step1Fields: FunctionComponent<Step1FieldsProps> = ({ onStepSuccess, onCloseChallenge }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useFormContext<ModalFormData>();

  const { control } = form;

  const hasStartDate = form.watch('has_start_date');
  const challengeStatus = form.watch('status');

  const handleCancel = () => {
    navigate('/application/challenge');
  };

  const handleNext = async () => {
    const isValid = await form.trigger();

    if (isValid) {
      form.setValue('step', 2);
      onStepSuccess?.();
    }
  };

  return (
    <Flex direction='column' gap={8} maxW={{ base: '100%', md: '50%', lg: '30%' }}>
      <Text fontSize='16px' fontWeight='medium'>
        {t('pages.challenges_details.tabs.tab_1.title')}
      </Text>

      <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
        control={control}
        name='team_uuid'
        prefetchOnOpen
        label={t('pages.challenges_details.tabs.tab_1.input_team_label')}
        placeholder={t('pages.challenges_details.tabs.tab_1.input_team_label')}
        showFlag
        width='100%'
        usePortal={false}
      />

      <InputField
        label={t('pages.challenges_details.tabs.tab_1.input_title_label')}
        helperText={`(${t('utils.components.input.max_chars', { chars: 50 })})`}
        maxLength={50}
        {...form.register('title')}
        disabled={challengeStatus !== ChallengeStatus.DRAFT}
        rootProps={{ isInvalid: !!form.formState.errors?.title, errorMessage: form.formState.errors?.title?.message }}
      />

      <TextareaField
        rootProps={{
          isInvalid: !!form.formState.errors?.description,
          errorMessage: form.formState.errors?.description?.message,
          css: {
            textarea: {
              marginTop: 2,
              height: '2em',
            },
          },
        }}
        label={
          <Flex direction='column'>
            <Text>{t('pages.challenges_details.tabs.tab_1.input_description_label')}</Text>
            <Text fontSize='14px' fontWeight='normal' color='gray.500'>
              ({t('pages.challenges_details.tabs.tab_1.input_description_helptext')})
            </Text>
          </Flex>
        }
        helperText={`(${t('utils.components.input.max_chars', { chars: 400 })})`}
        maxLength={400}
        disabled={challengeStatus !== ChallengeStatus.DRAFT}
        {...form.register('description')}
      />

      <InputField
        rootProps={{
          isInvalid: !!form.formState.errors?.time_limit_seconds,
          errorMessage: form.formState.errors?.time_limit_seconds?.message,
          sx: {
            input: {
              marginTop: { base: 9 },
            },
          },
        }}
        type='number'
        label={
          <Flex direction='column'>
            <Text>{t('pages.challenges_details.tabs.tab_1.input_limit_label')}</Text>
            <Text fontSize='14px' fontWeight='normal' color='gray.500'>
              ({t('pages.challenges_details.tabs.tab_1.input_limit_helptext')})
            </Text>
          </Flex>
        }
        disabled={challengeStatus !== ChallengeStatus.DRAFT}
        {...form.register('time_limit_seconds', { setValueAs: value => (value === '' ? undefined : parseInt(value, 10)) })}
      />

      <Flex direction='column'>
        <Controller
          name='has_start_date'
          control={form.control}
          render={({ field }) => (
            <FormControl alignSelf='center' display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium'>{t('pages.challenges_details.tabs.tab_1.switch_start_at_label')}</Text>

              <Switch
                name={field.name}
                ref={field.ref}
                isChecked={field.value}
                disabled={challengeStatus !== ChallengeStatus.DRAFT}
                onChange={event => {
                  field.onChange(event);
                  form.setValue('has_start_date', event.target.checked);
                }}
                onBlur={field.onBlur}
              />
            </FormControl>
          )}
        />

        <Text fontSize='14px' color='gray.500'>
          ({t('pages.challenges_details.tabs.tab_1.switch_start_at_helptext')})
        </Text>
      </Flex>

      {hasStartDate ? (
        <InputField
          style={{ display: hasStartDate ? 'block' : 'none' }}
          type='datetime-local'
          fullDatePickerArea
          label={t('pages.challenges_details.tabs.tab_1.input_date_label')}
          {...form.register('starts_in')}
          rootProps={{ isInvalid: !!form.formState.errors?.starts_in, errorMessage: form.formState.errors?.starts_in?.message }}
          disabled={challengeStatus !== ChallengeStatus.DRAFT}
        />
      ) : null}

      <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
        {challengeStatus === ChallengeStatus.PUBLISHED ? (
          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={onCloseChallenge}
            width={{ base: '100%', md: 'initial' }}
            p={0}
            color='brand.500'
            backgroundColor='transparent'
            textDecoration='underline'
            _hover={{
              backgroundColor: 'transparent',
              color: 'brand.500',
            }}
          >
            <Icon as={PiProhibit} color='brand.500' />
            {t('pages.challenges_details.button_end_challenge')}
          </Button>
        ) : null}

        <Flex w={'100%'} justifyContent='end' gap={4}>
          <Button variant='outlined' color='gray.600' onClick={handleCancel}>
            {challengeStatus === ChallengeStatus.PUBLISHED
              ? t('utils.components.button.close')
              : t('utils.components.button.cancel')}
          </Button>

          <Button onClick={handleNext} isLoading={form.formState.isValidating}>
            {t('utils.components.button.next')}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
